import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { memo, useCallback, useMemo, useState } from 'react';
import { type To, useHref, useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';

import { EntityTooltip } from './EntityTooltip';
import type {
  BaseEntity,
  EntityLinkTriggerProps,
  EntityTooltipContentProps,
  FetchState,
} from './types';

/**
 * RTK Query hook result interface
 * This types the return value of RTK Query hooks while remaining flexible
 */
interface RTKQueryResult<TData> {
  readonly data?: TData | undefined;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly isError: boolean;
  readonly isSuccess: boolean;
  readonly error?: SerializedError | FetchBaseQueryError | undefined;
}

/**
 * RTK Query hook type
 * Accepts query arg and options, returns typed result
 */
type RTKQueryHook<TData, TQueryArg> = (
  arg: TQueryArg,
  options?: { skip?: boolean },
) => RTKQueryResult<TData>;

/**
 * Configuration for creating an entity link
 */
export interface CreateEntityLinkConfig<
  TEntity extends BaseEntity,
  TQueryArg,
  TExtraProps = object,
> {
  /** Component to render tooltip content (should be memoized) */
  readonly tooltipContent: React.ComponentType<
    EntityTooltipContentProps<TEntity>
  >;

  /** Function to generate the detail page URL (optional - if not provided, renders as non-clickable) */
  readonly getDetailUrl?:
    | ((entityId: string, props: TExtraProps) => To | null)
    | null;

  /** RTK Query hook for fetching the entity */
  readonly useQuery: RTKQueryHook<TEntity, TQueryArg>;

  /** Function to convert entityId to query argument */
  readonly getQueryArg: (entityId: string, props: TExtraProps) => TQueryArg;

  /** Display name for React DevTools */
  readonly displayName: string;

  /** Skip query until tooltip opens (default: true) */
  readonly skipUntilOpen?: boolean;

  /** Default delay in ms before tooltip opens (default: 200) */
  readonly defaultOpenDelay?: number;
}

/**
 * Props for entity link components created by the factory
 */
export type EntityLinkProps<TExtraProps = object> = EntityLinkTriggerProps & {
  /** ID of the entity to link to */
  entityId: string;
} & TExtraProps;

/**
 * Factory function to create a self-contained entity link component
 *
 * This creates a complete entity link with:
 * - RTK Query integration with automatic caching
 * - Navigation to detail page (optional)
 * - Persistent tooltip behavior
 * - Full type safety
 * - Automatic subscription cleanup
 *
 * Each entity is self-contained with no need for a central registry.
 *
 * @example
 * ```tsx
 * // With navigation
 * export const FirmLink = createEntityLink({
 *   tooltipContent: FirmTooltipContent,
 *   getDetailUrl: (id) => `/firms/${id}`,
 *   useQuery: useGetFirmQuery,
 *   getQueryArg: (id) => id,
 *   displayName: 'FirmLink',
 * });
 *
 * // Without navigation (tooltip only)
 * export const PreviewOnlyLink = createEntityLink({
 *   tooltipContent: EntityTooltipContent,
 *   useQuery: useGetEntityQuery,
 *   getQueryArg: (id) => id,
 *   displayName: 'PreviewOnlyLink',
 * });
 * ```
 */
export const createEntityLink = <
  TEntity extends BaseEntity,
  TQueryArg = string,
  TExtraProps = object,
>({
  tooltipContent: TooltipContent,
  getDetailUrl,
  useQuery,
  getQueryArg,
  displayName,
  skipUntilOpen = true,
  defaultOpenDelay = 200,
}: CreateEntityLinkConfig<TEntity, TQueryArg, TExtraProps>) => {
  const EntityLink = (props: EntityLinkProps<TExtraProps>) => {
    const {
      entityId,
      children,
      disableNavigation = false,
      openInNewTab = false,
      className,
      side = 'right',
      align = 'center',
      variant = 'text',
      openDelay = defaultOpenDelay,
      ...restProps
    } = props;

    const extraProps = restProps as unknown as TExtraProps;
    const navigate = useNavigate();
    const [shouldFetch, setShouldFetch] = useState(!skipUntilOpen);

    // Derive label for badge variant from displayName (e.g. "TaskLink" -> "Task")
    const badgeLabel = useMemo(
      () => displayName.replace(/Link$/, '').toUpperCase(),
      [],
    );

    // Use RTK Query hook with proper skip behavior
    const queryArg = useMemo(
      () => getQueryArg(entityId, extraProps),
      [entityId, extraProps],
    );
    const { data, isLoading, isFetching, isError, error, isSuccess } = useQuery(
      queryArg,
      {
        skip: !shouldFetch,
      },
    );

    // Convert RTK Query state to our FetchState discriminated union
    const fetchState = useMemo<FetchState<TEntity>>(() => {
      if (isError && error) {
        return { status: 'error', error };
      }
      if (isSuccess && data) {
        return { status: 'success', data };
      }
      if (isLoading || isFetching) {
        return { status: 'pending' };
      }
      return { status: 'idle' };
    }, [isLoading, isFetching, isError, isSuccess, error, data]);

    // Memoize tooltip content renderer
    const tooltipContentRenderer = useCallback(
      (isPersistent: boolean) => (
        <TooltipContent fetchState={fetchState} isPersistent={isPersistent} />
      ),
      [fetchState],
    );

    // Detail URL (optional)
    const detailUrl = useMemo(
      () => (getDetailUrl ? getDetailUrl(entityId, extraProps) : null),
      [entityId, extraProps],
    );
    const hasNavigation = Boolean(detailUrl && !disableNavigation);
    const href = useHref(detailUrl || '');

    // Handle navigation
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (!hasNavigation || !detailUrl) {
          e.preventDefault();
          return;
        }

        // Allow cmd/ctrl+click for new tab or if openInNewTab is true
        if (e.metaKey || e.ctrlKey || openInNewTab) {
          return;
        }

        e.preventDefault();
        navigate(detailUrl);
      },
      [hasNavigation, navigate, detailUrl, openInNewTab],
    );

    // Trigger element - styled differently based on whether it's navigable and the variant
    const triggerContent =
      variant === 'badge' ? (
        <span
          className={cn(
            'inline-flex h-4 max-w-[200px] items-center overflow-hidden rounded-[4px] border align-middle transition-all duration-200',
            hasNavigation
              ? 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50 hover:shadow-sm'
              : 'border-gray-200 bg-gray-50/50 text-gray-500',
          )}
        >
          <span className="flex h-full shrink-0 items-center border-r border-gray-100 bg-gray-50 px-1 text-[9px] font-bold uppercase leading-none tracking-wider text-gray-400">
            {badgeLabel}
          </span>
          <span className="flex h-full min-w-0 items-center px-1.5 text-[10px] font-medium leading-none text-gray-700">
            <span className="truncate">{children}</span>
          </span>
        </span>
      ) : (
        children
      );

    const trigger =
      hasNavigation && detailUrl ? (
        <a
          href={href}
          onClick={handleClick}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          className={cn(
            variant === 'text' && [
              'inline-flex cursor-pointer items-center gap-0.5',
              'text-gray-900 font-medium',
              'underline decoration-gray-300 decoration-dotted underline-offset-2',
              'hover:text-blue-600 hover:decoration-blue-400 hover:decoration-solid',
              'transition-colors duration-150',
            ],
            variant === 'badge' && ['cursor-pointer decoration-0'],
            'transition-colors',
            className,
          )}
          data-entity-id={entityId}
          data-entity-type={displayName}
        >
          {triggerContent}
        </a>
      ) : (
        <span
          className={cn(
            variant === 'text' && [
              'inline-flex items-center gap-0.5',
              'text-gray-500 font-medium',
              'border-b border-dotted border-gray-300',
              'cursor-help',
            ],
            variant === 'badge' && ['cursor-help decoration-0'],
            'transition-colors',
            className,
          )}
          data-entity-id={entityId}
          data-entity-type={displayName}
        >
          {triggerContent}
        </span>
      );

    return (
      <EntityTooltip
        content={tooltipContentRenderer}
        side={side}
        align={align}
        openDelay={openDelay}
        onOpenChange={(open) => {
          if (open && skipUntilOpen) {
            setShouldFetch(true);
          }
        }}
      >
        {trigger}
      </EntityTooltip>
    );
  };

  EntityLink.displayName = displayName;

  // Return memoized component for better performance
  return memo(EntityLink);
};
